import type {Reducer, Effect, Subscription} from 'umi';
import {Modal} from 'antd';

import {FILE_PATH, WSS_URL} from '@/constant';
import {uploadFile} from '@/services/basic';
import {finishInquiry, getInquiryCountdown, getInquiryList, getTopic, sendInquiryMessage} from '@/services/inquiry';
import type {ImEndpoint, ImMessage, OnlineInquiryRecord} from '@/pages/Inquiry/data.d';
import {OnlineInquiryStatusEnum} from '@/pages/Inquiry/data.d';
import {getInquiryDetail, getServersEndpoint, getServersHistory} from '@/pages/Inquiry/service';
import {cloneDeep} from 'lodash';
import { withModalRootContainer } from '@/providers/AntdProvider';

export interface InquiryState {
  inquiryList: OnlineInquiryRecord[],
  currentInquiryIndex?: number,
  currentInquiry?: OnlineInquiryRecord,

  imEndpoint?: ImEndpoint;
  inquiryHistory: ImMessage[];
  countdown?: number;

  inquiryModalVisible?: boolean;
};

let countdownTimer: NodeJS.Timeout | null = null;
let onlineInquiryWssTimer: NodeJS.Timeout | null = null;
let serviceConsultWssTimer: NodeJS.Timeout | null = null;
let serviceInquiryWssTimer: NodeJS.Timeout | null = null;
let onlineInquiryWssRunning: boolean = false;
let serviceConsultWssRunning: boolean = false;
let serviceInquiryWssRunning: boolean = false;
let onlineInquiryWss: WebSocket | null = null;    // 在线问诊（就医咨询）
let serviceConsultWss: WebSocket | null = null;   // 服务咨询
let serviceInquiryWss: WebSocket | null = null;   // 指定人员发起咨询


const delay = (ms: number) => {
  return new Promise(resolve => {
    countdownTimer = setTimeout(resolve, ms);
  });
};

export interface InquiryType {
  namespace: 'inquiry',
  state: InquiryState,
  reducers: Record<string, Reducer>,
  effects: Record<string, Effect>,
  subscriptions: Record<string, Subscription>,
};

const InquiryModel: InquiryType = {
  namespace: 'inquiry',
  state: {
    inquiryList: [],
    inquiryHistory: [],
    inquiryModalVisible: false,
  },
  reducers: {
    setInquiryList(state, {payload}) {
      state.inquiryList = payload;
    },
    setCurrentInquiryIndex(state, {payload}) {
      state.currentInquiryIndex = payload;
      state.currentInquiry = payload !== undefined ? {...state.inquiryList[payload]} : undefined;
    },
    setImEndPoint(state, {payload}) {
      state.imEndpoint = payload;
    },
    setInquiryHistory(state, {payload}) {
      state.inquiryHistory = payload;
    },
    pushInquiryHistory(state, {payload}) {
      state.inquiryHistory.push(payload);
    },
    setCountdown(state, {payload}) {
      state.countdown = payload;
    },
    resetCountdown(state) {
      clearTimeout(countdownTimer as NodeJS.Timeout);
      countdownTimer = null;
      state.countdown = undefined;
    },
    setModalVisible(state, {payload}) {
      state.inquiryModalVisible = payload;
    },
  },
  effects: {
    *onInquirySiderListClick({payload}, {put, select}) {
      yield put({type: 'resetChatRoom'});
      yield put({type: 'setInquiryModalVisible', payload: false});
      if (payload !== undefined) {
        yield put({type: 'initChatRoom', payload});
      }
    },

    *resetChatRoom(action, {call, put, select}) {
      yield put({type: 'setCurrentInquiryIndex', payload: undefined});
      yield put({type: 'setImEndPoint', payload: undefined});
      yield put({type: 'setInquiryHistory', payload: []});
    },

    *initChatRoom({payload}, {call, put, select}) {
      try {
        yield put({type: 'setCurrentInquiryIndex', payload});
        yield put({type: 'initCountdown'});
        const currentInquiry: OnlineInquiryRecord = yield select((state: any) => state.inquiry.currentInquiry);
        if (currentInquiry) {
          // 清除未读消息
          const inquiryList: OnlineInquiryRecord[] = yield select((state: any) => state.inquiry.inquiryList);
          const tempInquiryList = [...inquiryList];
          const targetIndex = inquiryList.findIndex(item => item.inquiryId === currentInquiry?.inquiryId);
          if (targetIndex !== -1) {
            const target = {...inquiryList[targetIndex]};
            target.imMessageBox = Object.assign({}, target.imMessageBox, {unread: 0});
            tempInquiryList[targetIndex] = target;
            yield put({type: 'setInquiryList', payload: tempInquiryList});
          }
          // 获取当前聊天室 imEndpoint 和 历史消息记录
          const {type, inquiryId} = currentInquiry;
          const {code, data} = yield call(getServersEndpoint, {type, inquiryId});
          if (code === 200) {
            yield put({type: 'sendAllRead', payload:data.imId});
            yield put({type: 'setImEndPoint', payload: data});
            yield put({type: 'getInquiryHistory', payload: {isLoadMore: false}});
            return;
          }
        }
      } catch (error) {
        console.log(error)
      }
      // message.error('获取终端信息失败，请稍后重试');
    },

    *sendMessage({payload, callback}, {call, put, select}) {
      const {type}: OnlineInquiryRecord = yield select((state: any) => state.inquiry.currentInquiry);
      const {eid, imId}: ImEndpoint = yield select((state: any) => state.inquiry.imEndpoint);
      const countdown: number = yield select((state: any) => state.inquiry.countdown);
      try {
        if (!countdown) yield put({type: 'initCountdown'});
        const {code} = yield call(sendInquiryMessage, {type, eid, imId, content: payload});
        if (code === 200) {
          callback?.(true);

        } else {
          callback?.(false);
        }
      } catch (error) {
        callback?.(false);
      }
    },

    *sendImage({payload, callback}, {call, put, select}) {
      try {
        const {code, data} = yield call(uploadFile, {file: payload, path: FILE_PATH.INQUIRY_IMAGE});
        if (code === 200) {
          const content = JSON.stringify({
            type: 'image',
            value: data
          });
          yield put({type: 'sendMessage', payload: content, callback});
        } else {
          callback?.(false);
        }
      } catch (error) {
        callback?.(false);
      }
    },

    *finish(action, {call, put, select}) {
      const currentInquiry: OnlineInquiryRecord = yield select((state: any) => state.inquiry.currentInquiry);
      if (!currentInquiry) {
        yield put({type: 'getInquiryList'});
        return
      }
      try {
        const {inquiryId} = currentInquiry;
        const {code, data} = yield call(finishInquiry, {inquiryId});
        if ( [1025002,200].includes(code)) {
          yield put({type: 'resetCountdown'});
          yield put({type: 'resetChatRoom'});
          yield put({type: 'getInquiryList'});
        }
      } catch (error) {
        console.log(error);
      }
    },

    *countdownTimer({payload}, {put, select}) {
      const countdown: number = yield select((state: any) => state.inquiry.countdown);
      if (countdown > 0) {
        yield delay(1000);
        yield put({type: 'setCountdown', payload: countdown - 1});
        yield put({type: 'countdownTimer'});
      } else {
        const currentInquiry: OnlineInquiryRecord = yield select((state: any) => state.inquiry.currentInquiry);
        yield put({type: 'resetCountdown'});
        withModalRootContainer(Modal.warning)({
          title: '咨询超时',
          content: `${currentInquiry.familyInfo?.name}的咨询已超时`
        })
        yield put({type: 'resetChatRoom'});
        yield put({type: 'getInquiryList'});
      }
    },

    *initCountdown(action, {call, put, select}) {
      yield put({type: 'resetCountdown'})
      try {
        const currentInquiry: OnlineInquiryRecord = yield select((state: any) => state.inquiry.currentInquiry);
        const {inquiryId} = currentInquiry;
        const {code, data} = yield call(getInquiryCountdown, {inquiryId});
        if (code === 200 && data !== null) {
          yield put({type: 'setCountdown', payload: data});
          yield put({type: 'countdownTimer'});
        }
      } catch (error) {
        console.log(error)
      }
    },

    *getInquiryHistory({payload}, {call, put, select}) {
      const {isLoadMore} = payload;
      try {
        const imEndpoint: ImEndpoint = yield select((state: any) => state.inquiry.imEndpoint);
        const inquiryHistory: ImMessage[] = yield select((state: any) => state.inquiry.inquiryHistory);
        const cursor = inquiryHistory?.[0]?.id || '';
        const {eid, imId} = imEndpoint;
        const {code, data} = yield call(getServersHistory, {eid, imId, cursor});
        if (code === 200) {
          if (!isLoadMore && data.length>1) {
            data[data.length - 1].isNewMsg = true;
          }
          const tempInquiryHistory = cloneDeep(inquiryHistory);
          if (isLoadMore && tempInquiryHistory.length>1) {
            tempInquiryHistory[tempInquiryHistory.length - 1].isNewMsg = false;
          }

          tempInquiryHistory.unshift(...data);
          yield put({type: 'setInquiryHistory', payload: tempInquiryHistory});
        }
      } catch (error) {
        console.log(error)
      }
    },

    *updateInquiryList({payload}, {call, put, select}) {
      const {attachment, id: messageId, imId, eid, content, timestamp} = payload;
      const inquiryList: OnlineInquiryRecord[] = yield select((state: any) => state.inquiry.inquiryList);
      const currentInquiry: OnlineInquiryRecord = yield select((state: any) => state.inquiry.currentInquiry);
      const imEndpoint: ImEndpoint = yield select((state: any) => state.inquiry.imEndpoint);
      const tempInquiryList = [...inquiryList];
      const parsedContent = JSON.parse(content);
      try {
        const biz = attachment?.biz?.biz;
        const bizList = ['oi', 'oi_server'];
        if (bizList.includes(biz)) {
          const inquiryId = attachment.biz.id;
          const targetIndex = inquiryList.findIndex(item => item.inquiryId === inquiryId);

          if (targetIndex !== -1) {
            const target = {...inquiryList[targetIndex]};
            target.status = (imEndpoint && (imEndpoint.eid === eid))
              ? OnlineInquiryStatusEnum['咨询中']
              : target.status;
            if (target?.imMessageBox?.timestamp as number >= timestamp) return;
            let unread = target.imMessageBox?.unread || 0;
            if (inquiryId !== currentInquiry?.inquiryId) {
              if (messageId !== target.imMessageBox?.messageId) {
                unread++;
              }
            }
            target.imMessageBox = Object.assign(
              {},


              target.imMessageBox,
              {messageId, imId, eid, timestamp, content, unread}
            )
            tempInquiryList[targetIndex] = target;
            yield put({type: 'setInquiryList', payload: tempInquiryList});
          } else if (parsedContent.type === 'text') {
            const {code, data} = yield call(getInquiryDetail, {inquiryId});
            if (code === 200) {
              const {familyInfo, imMessageBox, type} = data;
              tempInquiryList.unshift({
                inquiryId,
                type,
                imMessageBox,
                familyInfo,
                status: OnlineInquiryStatusEnum.待接诊
              } as OnlineInquiryRecord);
              yield put({type: 'setInquiryList', payload: tempInquiryList});
            }
          }
        }
      } catch (error) {
        console.log(error)
      }
    },

    *messageHandler({payload}, {put, select}) {
      const {imId, content,socketObj} = payload;
      const imEndpoint: ImEndpoint = yield select((state: any) => state.inquiry.imEndpoint);
      const countdown: number = yield select((state: any) => state.inquiry.countdown);
      try {
        const contentObj = JSON.parse(content)
        if (contentObj.type === 'order') {

          if ('INQUIRY_REFRESH' === contentObj.value) {
            yield put({type: 'allRead', payload})
          }
          if ('inquiry_end' === contentObj.value) {
            yield put({type: 'finish'})
            return
          }
          if ('inquiry_start' === contentObj.value && !countdown && imId === imEndpoint?.imId) {
            yield put({type: 'initCountdown'})
            return
          }
          return
        }
      } catch (e) {

      }

      if (imId === imEndpoint?.imId) {
        socketObj?.send("hm:inquiry:refresh:"+imId)
        payload.isNewMsg = true;
        yield put({type: 'pushInquiryHistory', payload});
      }
      yield put({type: 'updateInquiryList', payload})
    },
    sendAllRead({payload}){

      onlineInquiryWss?.send("hm:inquiry:refresh:"+payload)
    },
    *allRead({payload}, {put, select}) {
      const {imId,} = payload;
      const inquiryList: OnlineInquiryRecord[] = yield select((state: any) => state.inquiry.inquiryList);
      const [_, inquiryId] = imId.split('@')
      const targetIndex = inquiryList.findIndex(item => item.inquiryId === inquiryId);
      if (inquiryList[targetIndex]?.imMessageBox?.unread) {
        const newList = cloneDeep(inquiryList)
        // @ts-ignore
        newList[targetIndex].imMessageBox.unread = 0;
        newList[targetIndex].status = OnlineInquiryStatusEnum['咨询中'];
        yield put({type: 'setInquiryList', payload: newList});
      }


    },

    *getInquiryList(action, {call, put}) {
      let inquiryList = [];
      try {
        const {code, data} = yield call(getInquiryList);
        if (code === 200) {
          inquiryList = data;
        }
      } catch (error) {
      }
      yield put({type: 'setInquiryList', payload: inquiryList});
      // yield put({ type: 'initWebsocket' });
    },

    *setInquiryModalVisible({payload}, {put}) {
      yield put({type: 'getInquiryList'});
      yield put({type: 'setModalVisible', payload});
    },
  },
  subscriptions: {
    setup({history, dispatch}) {
      // console.log(action);
      // let a = 1;

      const clearOnlineInquiryWebsocket = () => {
        if (onlineInquiryWssTimer) {
          clearInterval(onlineInquiryWssTimer)
        }
        onlineInquiryWss = null;
        onlineInquiryWssRunning = false;
      };

      const initOnlineInquiryWebsocket = async () => {
        if (onlineInquiryWssRunning) return;
        onlineInquiryWssRunning = true;
        const {data: topic} = await getTopic({type: 1});
        onlineInquiryWss = new WebSocket(WSS_URL);
        onlineInquiryWss.onopen = () => {
          onlineInquiryWss?.send(topic);
        };
        onlineInquiryWss.onmessage = (msg) => {

          dispatch({type: 'messageHandler', payload: {...JSON.parse(msg.data),socketObj:onlineInquiryWss}});
        };
        onlineInquiryWss.onerror = (err) => console.log('wss error', err);
        onlineInquiryWss.onclose = (err) => console.log('wss close', '断开连接', console.log(err));
        // 保持 ping
        if (onlineInquiryWssTimer) clearInterval(onlineInquiryWssTimer);
        onlineInquiryWssTimer = setInterval(() => {
          try {
            // 判断wss 状态，如果正在连接中则无视
            // 如果断联，则重新连接
            switch (onlineInquiryWss?.readyState) {
              case 0:
                break;
              case 1:
                onlineInquiryWss?.send('ping');
                break;
              default:
                clearOnlineInquiryWebsocket();
                dispatch({type: 'getInquiryList'});
                initOnlineInquiryWebsocket();
                break;
            }
          } catch (error) {
          }
        }, 55 * 1000);
      };

      const clearServiceConsultWebsocket = () => {
        if (serviceConsultWssTimer) {
          clearInterval(serviceConsultWssTimer)
        }
        serviceConsultWss = null;
        serviceConsultWssRunning = false;
      };

      const initServiceConsultWebsocket = async () => {
        if (serviceConsultWssRunning) return;
        serviceConsultWssRunning = true;
        const {data: topic} = await getTopic({type: 2});
        serviceConsultWss = new WebSocket(WSS_URL);
        serviceConsultWss.onopen = () => {
          serviceConsultWss?.send(topic);
        };
        serviceConsultWss.onmessage = (msg) => {
          dispatch({type: 'messageHandler', payload: {...JSON.parse(msg.data),socketObj:serviceConsultWss}});
        };
        serviceConsultWss.onerror = (err) => console.log('wss error', err);
        serviceConsultWss.onclose = (err) => console.log('wss close', '断开连接', console.log(err));
        // 保持 ping
        if (serviceConsultWssTimer) clearInterval(serviceConsultWssTimer);
        serviceConsultWssTimer = setInterval(() => {
          try {
            // 判断wss 状态，如果正在连接中则无视
            // 如果断联，则重新连接
            switch (serviceConsultWss?.readyState) {
              case 0:
                break;
              case 1:
                serviceConsultWss?.send('ping');
                break;
              default:
                clearServiceConsultWebsocket();
                dispatch({type: 'getInquiryList'});
                initServiceConsultWebsocket();
                break;
            }
          } catch (error) {
          }
        }, 55 * 1000);
      };

      const clearServiceInquiryWebsocket = () => {
        if (serviceInquiryWssTimer) clearInterval(serviceInquiryWssTimer);
        serviceInquiryWss = null;
        serviceInquiryWssRunning = false;
      };
      const initServiceInquiryWebsocket = async () => {
        if (serviceInquiryWssRunning) return;
        serviceInquiryWssRunning = true;
        const {data: topic} = await getTopic({type: 3});
        serviceInquiryWss = new WebSocket(WSS_URL);
        serviceInquiryWss.onopen = () => {
          serviceInquiryWss?.send(topic);
        };
        serviceInquiryWss.onmessage = msg => {
          dispatch({type: 'messageHandler', payload:  {...JSON.parse(msg.data),socketObj:serviceInquiryWss}});
        };
        serviceInquiryWss.onerror = (err) => console.log('wss error', err);
        serviceInquiryWss.onclose = (err) => console.log('wss close', '断开连接', console.log(err));
        // 保持 ping
        if (serviceInquiryWssTimer) clearInterval(serviceInquiryWssTimer);
        serviceInquiryWssTimer = setInterval(() => {
          try {
            // 判断wss 状态，如果正在连接中则无视
            // 如果断联，则重新连接
            switch (serviceInquiryWss?.readyState) {
              case 0:
                break;
              case 1:
                serviceInquiryWss?.send('ping');
                break;
              default:
                clearServiceInquiryWebsocket();
                dispatch({type: 'getInquiryList'});
                initServiceInquiryWebsocket();
                break;
            }
          } catch (error) {
          }
        }, 55 * 1000);
      }

      return history.listen(({pathname}) => {
        if (pathname.toLowerCase() !== '/user/login') {
          dispatch({type: 'getInquiryList'});
          initOnlineInquiryWebsocket();
          initServiceConsultWebsocket();
          initServiceInquiryWebsocket();
        } else {
          clearOnlineInquiryWebsocket();
          clearServiceConsultWebsocket();
          clearServiceInquiryWebsocket();
        }
      })
    }
  }
};

export default InquiryModel;
